/**
 * Centralized, typed access to runtime configuration.
 *
 * Everything the app needs from the environment funnels through here so the
 * rest of the codebase never touches `import.meta.env` directly. Defaults point
 * at the Vite dev-proxy paths (see vite.config.ts); production builds override
 * them via `.env.production`.
 */

function required(value: string | undefined, name: string): string {
  if (!value) {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return value;
}

export const env = {
  /** Auth/OAuth server (Laravel 11 + Passport) — JSON API base (.../api). */
  authBaseUrl: required(import.meta.env.VITE_AUTH_BASE_URL, 'VITE_AUTH_BASE_URL'),
  /** OAuth server ORIGIN — /oauth/authorize + /oauth/token live at the root, not under /api. */
  authOrigin: new URL(import.meta.env.VITE_AUTH_BASE_URL ?? '/', window.location.origin).origin,
  /** Public Passport client id for the SPA (authorization_code + PKCE, no secret). */
  authPublicClientId: import.meta.env.VITE_AUTH_PUBLIC_CLIENT_ID ?? '',
  /** Backend website service (Laravel 10) — login, register, password reset. */
  backendBaseUrl: required(import.meta.env.VITE_BACKEND_BASE_URL, 'VITE_BACKEND_BASE_URL'),
  /** MEDIS real-time service (Azure) — appointments, schedules. */
  medisBaseUrl: required(import.meta.env.VITE_MEDIS_BASE_URL, 'VITE_MEDIS_BASE_URL'),
  /**
   * MEDIS **v2** API static base URL (`X-ZUMO-AUTH` calls: /v2/GetPatient, ...).
   *
   * Only the FALLBACK: v2 calls resolve their host/path through the dynamic
   * endpoint registry (`api_version: "v2"` rows of `/v2/endpoint-urls`, see
   * `src/lib/medis/endpointUrls.ts`) and land here when no v2 row matches -
   * the same role `medisBaseUrl` plays for the v1 calls.
   */
  medisV2BaseUrl: import.meta.env.VITE_MEDIS_V2_BASE_URL ?? 'https://api-v10.grlm.ro/api',
  /**
   * Whether to load MEDIS endpoint overrides from the auth server's
   * `/endpoint-urls` (dynamic routing). When disabled, every MEDIS call uses
   * the static `medisBaseUrl`. Default: enabled (set VITE_MEDIS_DYNAMIC_ENDPOINTS=false to turn off).
   */
  medisDynamicEndpoints: import.meta.env.VITE_MEDIS_DYNAMIC_ENDPOINTS !== 'false',

  enableQueryDevtools: import.meta.env.VITE_ENABLE_QUERY_DEVTOOLS === 'true',

  /**
   * Dev test tools (RegisterPatient scenario simulator + ZUMO session reset).
   * Always on under the dev server; for an uploaded build use
   * `npm run build:dev` (mode `devtools`, sets VITE_ENABLE_DEV_TOOLS=true).
   * Never enable in the real production build.
   */
  enableDevTools: import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEV_TOOLS === 'true',

  social: {
    googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '',
    facebookAppId: import.meta.env.VITE_FACEBOOK_APP_ID ?? '',
    appleClientId: import.meta.env.VITE_APPLE_CLIENT_ID ?? '',
  },

  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const;
